import React from 'react';
import { AlertTriangle, ShieldAlert, ShieldCheck, RotateCcw } from 'lucide-react';

interface FieldWrapperProps {
  label: string;
  error?: string;
  isOverridden?: boolean;
  onToggleOverride?: () => void;
  readOnly?: boolean;
  hint?: string;
  children: React.ReactNode;
  showIcon?: boolean;
}

export const FieldWrapper: React.FC<FieldWrapperProps> = ({
  label,
  error,
  isOverridden,
  onToggleOverride,
  readOnly,
  hint,
  children,
  showIcon = true
}) => {
  return (
    <div className="mb-6 group">
      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
        {label}
      </label>
      
      <div className="relative">
        {children}

        {/* Validation Status Icon */}
        {showIcon && error && !isOverridden && (
           <AlertTriangle className={`absolute right-3 top-2.5 h-5 w-5 ${readOnly ? 'text-red-400' : 'text-red-500'}`} />
        )}
        {showIcon && error && isOverridden && (
           <ShieldAlert className={`absolute right-3 top-2.5 h-5 w-5 ${readOnly ? 'text-amber-500' : 'text-amber-600'}`} />
        )}
      </div>

      {/* Error / Override Message Area */}
      {error && (
        <div className="mt-2 flex items-start justify-between">
          <div className={`text-sm flex items-center ${isOverridden ? 'text-amber-700' : 'text-red-600'}`}>
            <span className="mr-1.5">{isOverridden ? '⚠️ Manually Overridden:' : '●'}</span>
            {error}
          </div>
          
          {!readOnly && onToggleOverride && (
            <button
              onClick={onToggleOverride}
              className={`text-xs font-medium px-2 py-1 rounded-md transition-colors flex items-center space-x-1 border shadow-sm ${
                isOverridden 
                  ? 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50' 
                  : 'bg-white text-red-600 border-red-200 hover:bg-red-50'
              }`}
            >
              {isOverridden ? (
                <>
                  <RotateCcw className="w-3 h-3" />
                  <span>Undo</span>
                </>
              ) : (
                <>
                  <ShieldCheck className="w-3 h-3" />
                  <span>Accept Anyway</span>
                </>
              )}
            </button>
          )}
        </div>
      )}
      
      {hint && !error && (
        <p className="mt-1 text-xs text-slate-400">{hint}</p>
      )}
    </div>
  );
};